import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { Course } from '@/client/courses';
import { LanguageContext } from '@/common/context/LanguageContext';
import { useReplaceScheduledContent } from '@/hooks/query/course-customization.hooks';
import { useToast } from '@/hooks/useToast';
import customizationCompletedImage from '@/images/content/customization-completed.png';
import PagePlaceholderImage from '@/images/placeholders/page-template.svg';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { getTranslationByLanguage } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { Card } from 'primereact/card';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const CardsContainer = styled(FlexContainer)`
  width: 100%;
  max-width: 100%;
`;

const CourseCard = styled(Card)`
  &.p-card {
    border: 1px solid var(--gray-dark);
    border-radius: 19px;
    box-shadow: none;
    overflow: hidden;
    width: 278px;
    flex-shrink: 0;

    .p-card-body {
      padding: 27px 26px;
      display: flex;
      flex-direction: column;
      gap: 28px;
    }

    .p-card-header {
      aspect-ratio: 1920 / 1080;
      position: relative;
      border-radius: 10px;
      overflow: hidden;
    }
  }
`;

const ThumbnailImage = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--gray-pale);
`;

const CardContent = styled(FlexContainer)`
  gap: 10px;
`;

const CourseTitle = styled.div`
  font-family: var(--font-family);
  font-weight: 500;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  white-space: pre-wrap;
`;

const CourseDescription = styled.div`
  font-family: var(--font-family);
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  color: var(--ui-dark-grey, #707070);
  white-space: pre-wrap;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;
  align-self: stretch;
`;

const ConfirmationSection = styled(FlexContainer)`
  gap: 36px;
  width: 100%;
  max-width: 100%;
`;

const ConfirmationTitle = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: 24px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
`;

const ConfirmationDescription = styled.div`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  text-align: center;
  white-space: pre-wrap;

  p {
    margin: 0;
  }
`;

const EnrolledUsersNote = styled.div`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: var(--ui-dark-grey, #707070);
  text-align: center;
`;

const SuccessContainer = styled(FlexContainer)`
  gap: 36px;
  width: 100%;
  max-width: 100%;
  padding-top: 36px;
`;

const IllustrationImage = styled.div`
  position: relative;
  width: 278px;
  height: 278px;
  flex-shrink: 0;

  img {
    position: absolute;
    inset: 0;
    max-width: none;
    object-fit: cover;
    pointer-events: none;
    width: 100%;
    height: 100%;
  }
`;

const SuccessTitle = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: 24px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  text-align: center;
  white-space: pre-wrap;
`;

const SuccessDescription = styled.div`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  text-align: center;
  white-space: pre-wrap;

  p {
    margin: 0;
  }
`;

const SuccessNote = styled.div`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: var(--ui-dark-grey, #707070);
  text-align: center;
`;

export type ReplaceScheduledContentProps = {
  accountId: string;
  originalCourse: Course;
  customizedCourse: Course;
  onCancel: () => void;
  onGoToTrainingPlan?: () => void;
};

export const ReplaceScheduledContent: React.FC<
  ReplaceScheduledContentProps
> = ({
  accountId,
  originalCourse,
  customizedCourse,
  onCancel,
  onGoToTrainingPlan,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isReplaced, setIsReplaced] = useState(false);
  const { language } = useContext(LanguageContext);

  const { mutate: replaceScheduledContent, isLoading: isReplacing } =
    useReplaceScheduledContent({
      onSuccess: () => {
        toast?.success(
          t('toast.success'),
          t('course.customization.replaceScheduledContent.success'),
        );
        setIsReplaced(true);
      },
      onError: (error) => {
        handleAxiosError(error as AxiosError, toast);
      },
    });

  const handleConfirm = () => {
    replaceScheduledContent({
      accountId,
      originalCourseId: originalCourse.id,
      customizedCourseId: customizedCourse.id,
    });
  };

  // Show success state after replacement
  if (isReplaced) {
    return (
      <SuccessContainer direction="column" align="center" gap={36}>
        <FlexContainer direction="column" gap={13} align="center">
          <IllustrationImage>
            <img
              src={customizationCompletedImage}
              alt="Scheduled content replaced"
            />
          </IllustrationImage>
          <FlexContainer direction="column" gap={37} align="center">
            <FlexContainer direction="column" gap={10} align="center">
              <SuccessTitle>
                {t('course.customization.replaceScheduledContent.successTitle')}
              </SuccessTitle>
              <SuccessDescription>
                <p>
                  {t(
                    'course.customization.replaceScheduledContent.successDescription',
                  )}
                </p>
              </SuccessDescription>
              <SuccessNote>
                {t(
                  'course.customization.replaceScheduledContent.enrolledUsersNote',
                )}
              </SuccessNote>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
        <FlexContainer gap={14} align="center" justify="center">
          <AppButton
            label={t('button.close')}
            onClick={onCancel}
            type="outlined"
          />
          <AppButton
            label={t('course.customization.complete.goToTrainingPlan')}
            onClick={onGoToTrainingPlan || onCancel}
          />
        </FlexContainer>
      </SuccessContainer>
    );
  }

  const originalThumbnail =
    originalCourse.courseThumbnail?.signedUrl ?? PagePlaceholderImage;
  const customizedThumbnail =
    customizedCourse.courseThumbnail?.signedUrl ?? PagePlaceholderImage;

  return (
    <FlexContainer direction="column" gap={32} align="center" width="100%">
      <CardsContainer gap={20} align="flex-start" justify="center">
        <CourseCard
          header={
            <ThumbnailImage
              style={{
                backgroundImage: `url(${CSS.escape(originalThumbnail)})`,
              }}
            />
          }
        >
          <CardContent direction="column" align="flex-start">
            <AppChip
              label={t(
                'course.customization.replaceScheduledContent.currentlyScheduled',
              )}
              type="secondary"
              size="xs-auto"
            />
            <CourseTitle>
              {getTranslationByLanguage(
                originalCourse?.courseTranslations,
                language as LanguagesEnum,
                originalCourse?.defaultLanguage,
              )?.name ?? originalCourse?.name}
            </CourseTitle>
            <CourseDescription>
              {t(
                'course.customization.replaceScheduledContent.currentlyScheduledDescription',
              )}
            </CourseDescription>
          </CardContent>
        </CourseCard>

        <ArrowIcon>
          <i className="pi pi-arrow-right" style={{ fontSize: '24px' }} />
        </ArrowIcon>

        <CourseCard
          header={
            <ThumbnailImage
              style={{
                backgroundImage: `url(${CSS.escape(customizedThumbnail)})`,
              }}
            />
          }
        >
          <CardContent direction="column" align="flex-start">
            <AppChip
              label={t(
                'course.customization.replaceScheduledContent.customizedVersion',
              )}
              type="orange"
              size="xs-auto"
            />
            <CourseTitle>
              {getTranslationByLanguage(
                customizedCourse?.courseTranslations,
                language as LanguagesEnum,
                customizedCourse?.defaultLanguage,
              )?.name ?? customizedCourse?.name}
            </CourseTitle>
            <CourseDescription>
              {t(
                'course.customization.replaceScheduledContent.customizedVersionDescription',
              )}
            </CourseDescription>
          </CardContent>
        </CourseCard>
      </CardsContainer>

      <ConfirmationSection direction="column" align="center" gap={36}>
        <FlexContainer direction="column" gap={10} align="center">
          <ConfirmationTitle>
            {t('course.customization.replaceScheduledContent.title')}
          </ConfirmationTitle>
          <ConfirmationDescription>
            <p>
              {t('course.customization.replaceScheduledContent.description')}
            </p>
          </ConfirmationDescription>
          <EnrolledUsersNote>
            {t(
              'course.customization.replaceScheduledContent.enrolledUsersNote',
            )}
          </EnrolledUsersNote>
        </FlexContainer>

        <FlexContainer gap={14} align="center" justify="center">
          <AppButton
            label={t('button.close')}
            onClick={onCancel}
            type="outlined"
          />
          <AppButton
            label={t(
              'course.customization.replaceScheduledContent.replaceButton',
            )}
            onClick={handleConfirm}
            isDisabled={isReplacing}
          />
        </FlexContainer>
      </ConfirmationSection>
    </FlexContainer>
  );
};
